import React, { useEffect, useState } from "react";
import { ActivityIndicator, Alert, StyleSheet, Text, View } from "react-native";
import * as Device from "expo-device";
import * as FileSystem from "expo-file-system";
import { Image } from "expo-image";

import ClearCacheButton from "./Components/ClearCacheButton";

import { formatSize } from "@/utils/files";

const CacheManagementScreen = (): React.JSX.Element => {
  const [cacheSize, setCacheSize] = useState<number | null>(null);
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const locateCacheDirectory = async (): Promise<string> => {
    const sampleImageUri = "https://picsum.photos/200/300";

    // Step 1: Cache an image using Expo Image
    await Image.prefetch(sampleImageUri);

    // Step 2: Get the local URI of the cached image
    const localUri = await Image.getCachePathAsync(sampleImageUri);

    if (localUri === null) {
      throw new Error("Failed to get local URI of cached image");
    }

    // Step 3: Extract the directory path from the local URI
    const directory = localUri.substring(0, localUri.lastIndexOf("/") + 1);

    // Step 4: Verify if the directory exists
    const dirInfo = await FileSystem.getInfoAsync(directory);

    if (!dirInfo.exists || !dirInfo.isDirectory) {
      throw new Error("Cache directory does not exist or is not a directory");
    }
    return directory;
  };

  const loadCacheInfo = async (cacheDirectory: string): Promise<void> => {
    try {
      const cacheContents = await FileSystem.readDirectoryAsync(cacheDirectory);
      let totalSize = 0;
      for (const file of cacheContents) {
        const fileInfo = await FileSystem.getInfoAsync(
          `${cacheDirectory}${file}`,
        );
        if (fileInfo.exists && fileInfo.size) {
          totalSize += fileInfo.size;
        }
      }
      setFileCount(cacheContents.length);
      setCacheSize(totalSize);
    } catch (error) {
      Alert.alert(
        "Error",
        "Failed to load cache information. Please try again later.",
        [{ text: "OK" }],
      );
    }
  };

  useEffect(() => {
    const initCacheDirectory = async (): Promise<void> => {
      setIsLoading(true);
      const catchDirectory = await locateCacheDirectory();
      await loadCacheInfo(catchDirectory);
      setIsLoading(false);
    };
    if (Device.osName === "iOS") {
      initCacheDirectory();
    }
  }, []);

  const clearAllCache = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await Image.clearDiskCache();
      await Image.clearMemoryCache();
      Alert.alert("Success", "Cleared all image cache.");
      setFileCount(0);
      setCacheSize(0);
    } catch (error) {
      Alert.alert("Error", "Failed to clear all cache.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <Text style={styles.cardText}>
          Cache files improve app performance by storing frequently accessed
          data locally. This reduces load times and network usage, resulting in
          a smoother user experience. Clearing cache occasionally can free up
          storage space on your device.
        </Text>
        <View style={styles.statsContainer}>
          <View style={styles.statBox}>
            {isLoading ? (
              <ActivityIndicator />
            ) : (
              <Text style={styles.statValue}>
                {fileCount !== null ? fileCount : "Unknown"}
              </Text>
            )}
            <Text style={styles.statLabel}>Files</Text>
          </View>
          <View style={styles.statBox}>
            {isLoading ? (
              <ActivityIndicator />
            ) : (
              <Text style={styles.statValue}>
                {cacheSize !== null ? formatSize(cacheSize) : "Unknown"}
              </Text>
            )}
            <Text style={styles.statLabel}>Total Size</Text>
          </View>
        </View>
      </View>
      <ClearCacheButton isClearing={false} onPress={clearAllCache} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
    padding: 16,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
  },
  cardText: {
    fontSize: 14,
    color: "#666",
    marginBottom: 16,
  },
  statsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  statBox: {
    flex: 1,
    backgroundColor: "#f0f0f0",
    borderRadius: 8,
    padding: 16,
    alignItems: "center",
    marginHorizontal: 4,
  },
  statValue: {
    fontSize: 18,
    fontWeight: "bold",
  },
  statLabel: {
    fontSize: 14,
    color: "#666",
  },
});

export default CacheManagementScreen;
